import React, { useState, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import BlobField from '../components/BlobField';
import api, { formatApiErrorDetail } from '../api';

/**
 * Reset Password — landing page for the `?token=...` link sent in the
 * password-reset email. Two-panel: new-password form → success confirmation.
 * The token comes from the URL query param and is POSTed with the new password
 * to `/auth/reset-password`. Missing/invalid tokens surface a clear error and
 * a link back to /forgot-password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = pw2.length > 0 && pw !== pw2;
  const canSubmit = !!token && pw.length >= 8 && pw === pw2 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr('');
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <BlobField />
      <div className="stage">
        <div className="card" data-testid="reset-card">
          {!token ? (
            <>
              <h1 className="heading" style={{ textAlign: 'left', fontSize: 23 }}>Missing reset link</h1>
              <p className="subcopy" style={{ textAlign: 'left', margin: '6px 0 24px' }}>
                This reset link is missing its token. Please request a new one.
              </p>
              <Link to="/forgot-password" className="btn-primary" data-testid="reset-request-new">
                Request a new link
              </Link>
            </>
          ) : !done ? (
            <>
              <Link to="/login" className="back-btn" aria-label="Back to Sign In" data-testid="reset-back-btn" style={{ marginBottom: 18 }}>
                <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                  <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              <h1 className="heading" style={{ textAlign: 'left', fontSize: 23 }}>Set a new password</h1>
              <p className="subcopy" style={{ textAlign: 'left', margin: '6px 0 24px' }}>
                Choose a strong password — at least 8 characters.
              </p>
              <form onSubmit={submit}>
                <div className="field" style={{ marginBottom: 16 }}>
                  <label className="field-label"><span>New password</span></label>
                  <input
                    type="password"
                    className="field-input"
                    placeholder="At least 8 characters"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="new-password"
                    data-testid="reset-pw-input"
                  />
                  {tooShort && (
                    <p style={{ color: 'var(--error)', fontSize: 12, margin: '6px 0 0', fontWeight: 700 }}>
                      Password must be at least 8 characters.
                    </p>
                  )}
                </div>
                <div className="field" style={{ marginBottom: 20 }}>
                  <label className="field-label"><span>Confirm password</span></label>
                  <input
                    type="password"
                    className="field-input"
                    placeholder="Re-type your new password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    data-testid="reset-pw2-input"
                  />
                  {mismatch && (
                    <p style={{ color: 'var(--error)', fontSize: 12, margin: '6px 0 0', fontWeight: 700 }}>
                      Passwords don't match.
                    </p>
                  )}
                </div>
                {err && (
                  <p style={{ color: 'var(--error)', fontSize: 13, margin: '0 0 12px', fontWeight: 700 }} data-testid="reset-error">
                    {err}
                  </p>
                )}
                <button type="submit" className="btn-primary" disabled={!canSubmit} data-testid="reset-submit-btn">
                  {busy ? 'Saving…' : 'Set new password'}
                </button>
              </form>
              <p className="signup-note" style={{ marginTop: 16 }}>
                <Link to="/login" data-testid="reset-back-to-signin">Back to Sign In</Link>
              </p>
            </>
          ) : (
            <div className="success-wrap" data-testid="reset-success-panel">
              <div className="check-circle">
                <svg viewBox="0 0 24 24" fill="none" width="32" height="32">
                  <path d="M5 13l5 5L20 7" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="success-title">Password updated</div>
              <p className="success-sub">Your password has been reset. Sign in with your new password to continue.</p>
              <button
                className="btn-primary"
                onClick={() => navigate('/login', { replace: true })}
                data-testid="reset-go-signin"
              >
                Go to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
